import { useId, useRef, useState } from 'react';
import { apiErrorText, useGetFsnbWorkQuery } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Drawer, DrawerBody, DrawerCloseButton, DrawerHeader } from '@shared/ui/Drawer';
import { IconCopy } from '@shared/ui/icons';
import { BASE_TYPE_TITLES, fullCode } from '../../normBaseLabels';
import { copy } from '../helpers';
import {
  CopyBtn,
  Err,
  Eyebrow,
  HeaderRow,
  Muted,
  Title,
  Titles,
} from '../styles';
import { WorkCardBody } from './WorkCardBody';
import { CompareBody } from './CompareBody';

/**
 * Карточка нормы. С `compareEdition` — сравнение «было → стало» с этой
 * редакцией (просмотр изменений загруженного дополнения).
 */
export function WorkCardDrawer({
  baseType,
  code,
  edition,
  editionTitle,
  compareEdition,
  onClose,
  onOpenResource,
  onOpenCoefficient,
}: {
  baseType: string;
  code: string;
  edition: string;
  editionTitle?: string;
  compareEdition?: string | null;
  onClose: () => void;
  onOpenResource?: (source: string, code: string) => void;
  onOpenCoefficient?: (code: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [copied, setCopied] = useState(false);
  const q = useGetFsnbWorkQuery({ base_type: baseType, code, edition });
  const prev = useGetFsnbWorkQuery(
    { base_type: baseType, code, edition: compareEdition ?? '' },
    { skip: !compareEdition },
  );
  const shown = fullCode(baseType, code);
  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} size="lg" initialFocusRef={titleRef}>
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>
              Норма {BASE_TYPE_TITLES[baseType] ? `· ${BASE_TYPE_TITLES[baseType]}` : ''}
              {editionTitle ? ` · ${editionTitle}` : ''}
            </Eyebrow>
            <Title id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              {shown}
              <CopyBtn
                type="button"
                onClick={() => {
                  copy(shown);
                  setCopied(true);
                }}
                title="Скопировать шифр"
              >
                <IconCopy size={14} />
                {copied ? 'Скопировано' : 'Шифр'}
              </CopyBtn>
              {compareEdition && <Badge $tone="warning">было → стало</Badge>}
            </Title>
          </Titles>
          <DrawerCloseButton onClick={onClose} />
        </HeaderRow>
      </DrawerHeader>
      <DrawerBody>
        {q.isLoading && <Muted>Загрузка…</Muted>}
        {q.error && <Err>{apiErrorText(q.error, 'Норму не удалось открыть.')}</Err>}
        {q.data && !compareEdition && (
          <WorkCardBody
            card={q.data.card}
            onOpenResource={onOpenResource}
            onOpenCoefficient={onOpenCoefficient}
          />
        )}
        {q.data && compareEdition && prev.data && (
          <CompareBody before={prev.data.card} after={q.data.card} />
        )}
        {q.data && compareEdition && prev.error && (
          // Нормы нет в действующей — она новая: показываем просто карточку.
          <WorkCardBody
            card={q.data.card}
            onOpenResource={onOpenResource}
            onOpenCoefficient={onOpenCoefficient}
          />
        )}
      </DrawerBody>
    </Drawer>
  );
}
